import { useState, useMemo, useEffect } from 'react';

import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { LoadFileImportLanding } from './load-file-import';
import { LoadFileImportWizard } from './load-file-import/import-wizard';
import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../../../../../backend/src/organisation/organisation-permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useRelativitySyncProgress from '@/api/hooks/useRelativitySyncProgress';
import useBuildRelativityParentRelationships, {
  type BuildRelativityParentRelationshipsResponse,
} from '@/api/mutations/useBuildRelativityParentRelationships';
import { useDownloadRelativityReportArtefact } from '@/api/mutations/useDownloadRelativityReportArtefact';
import useStartRelativitySync from '@/api/mutations/useStartRelativitySync';
import useGetConfiguredRelativityWorkspaces from '@/api/queries/useGetConfiguredRelativityWorkspaces';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import useGetRelativitySyncPreview from '@/api/queries/useGetRelativitySyncPreview';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { PermissionGate } from '@/hooks/useHasPermission';
import { useRelativityAuth } from '@/hooks/useRelativityAuth';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import {
  SettingsBackLink,
  SettingsContent,
  SettingsRow,
  SettingsSection,
  SettingsSectionFooter,
  SettingsSectionHeading,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import { Progress } from '@/new/components/shadcn/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

/**
 * Relativity settings. Unlike the old page, the top level is NOT gated behind a
 * Relativity login: the load file import feature needs no login, so the connect prompt
 * is scoped to the metadata-sync / relationship-building sections only.
 */
export function Relativity() {
  const { isEnabled } = useOrgPermissions();
  const isLoadFileImportEnabled = isEnabled(OrgPermission.RelativityLoadFileImport);

  return (
    <Routes>
      <Route index element={<RelativityLanding />} />
      <Route path="sync/:jobId" element={<RelativitySyncProgress />} />

      {isLoadFileImportEnabled && (
        <>
          <Route path="load-file-import" element={<LoadFileImportLanding />} />
          <Route path="load-file-import/:importId" element={<LoadFileImportWizard />} />
        </>
      )}
    </Routes>
  );
}

function RelativityLanding() {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { isEnabled } = useOrgPermissions();
  const isLoadFileImportEnabled = isEnabled(OrgPermission.RelativityLoadFileImport);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | undefined>();
  const [relationshipBuildResult, setRelationshipBuildResult] =
    useState<BuildRelativityParentRelationshipsResponse | null>(null);

  // Check Relativity authentication status
  const { isAuthenticated, isLoading: isLoadingAuth, isConnecting, connectRelativity } = useRelativityAuth();

  // Fetch matter data to check if it's linked to a Relativity workspace
  const { data: matter } = useGetMatterV2(matterId);

  // Fetch configured workspaces (only if authenticated)
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useGetConfiguredRelativityWorkspaces({
    enabled: isAuthenticated,
  });

  // Check if matter is linked to a specific workspace
  const linkedWorkspaceId = matter?.relativityWorkspaceId;
  const linkedWorkspace = useMemo(() => {
    if (!linkedWorkspaceId || !workspaces) return null;
    return workspaces.find((w) => w.workspaceId === linkedWorkspaceId);
  }, [linkedWorkspaceId, workspaces]);

  // Auto-select the linked workspace when available
  useEffect(() => {
    if (linkedWorkspaceId && !selectedWorkspaceId) {
      setSelectedWorkspaceId(linkedWorkspaceId);
    }
  }, [linkedWorkspaceId, selectedWorkspaceId]);

  useEffect(() => {
    setRelationshipBuildResult(null);
  }, [selectedWorkspaceId]);

  // Fetch preview when workspace selected
  const { data: preview, isLoading: isLoadingPreview } = useGetRelativitySyncPreview(matterId, selectedWorkspaceId, {
    enabled: !!selectedWorkspaceId,
  });

  // Start sync mutation
  const { mutateAsync: startSync, isLoading: isStarting } = useStartRelativitySync();
  const { mutateAsync: buildRelationships, isLoading: isBuildingRelationships } =
    useBuildRelativityParentRelationships();

  const handleStartSync = async () => {
    if (!matterId || !selectedWorkspaceId) {
      toast.error('Please select a workspace');
      return;
    }

    try {
      const result = await startSync({ matterId, workspaceId: selectedWorkspaceId });
      trackCustomEvent(TrackingEventsV2.RelativitySync, {
        matterId,
        workspaceId: selectedWorkspaceId,
        filesFound: result.filesFound,
        jobId: result.jobId,
      });
      toast.success(`Started syncing metadata for ${result.filesFound.toLocaleString()} files`);
      navigate(`sync/${result.jobId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start sync');
    }
  };

  const handleBuildRelationships = async () => {
    if (!matterId || !selectedWorkspaceId) {
      toast.error('Please select a workspace');
      return;
    }

    setRelationshipBuildResult(null);
    try {
      const result = await buildRelationships({ matterId, workspaceId: selectedWorkspaceId });
      trackCustomEvent(TrackingEventsV2.RelativityBuildParentChildRelationships, {
        matterId,
        workspaceId: selectedWorkspaceId,
        newRelationshipsCreated: result.newRelationshipsCreated,
        existingRelationshipsSkipped: result.existingRelationshipsSkipped,
      });
      setRelationshipBuildResult(result);
      const newRelationshipsLabel = result.newRelationshipsCreated === 1 ? 'relationship' : 'relationships';
      const skippedRelationshipsLabel = result.existingRelationshipsSkipped === 1 ? 'relationship' : 'relationships';
      toast.success(
        `Built ${result.newRelationshipsCreated.toLocaleString()} new ${newRelationshipsLabel}; skipped ${result.existingRelationshipsSkipped.toLocaleString()} existing ${skippedRelationshipsLabel}.`,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to build relationships');
    }
  };

  if (!matterId) return null;

  // The intro copy shared by the default setup view.
  const syncDescription = (
    <>
      Sync metadata from Relativity. This will update the metadata for all documents that were originally imported using
      the Relativity import feature with the latest values from your Relativity workspace. Only fields mapped in the{' '}
      <Link
        to="/organisation-settings/integrations/field-mappings"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600"
      >
        mapping configurator
        <Icon className="lucide--arrow-up-right" />
      </Link>{' '}
      will be imported.
    </>
  );

  // Connect-to-Relativity prompt, shown inline inside the sync section only.
  const connectPrompt = (
    <div className="px-5 py-6">
      <p className="mb-4 text-sm text-gray-500">
        To sync metadata or build relationships from Relativity, connect your Relativity account first. This opens a new
        window to authenticate with Relativity.
      </p>
      <PermissionGate
        permission={Permission.RelativityImport}
        uiMode="disabled"
        disabledMessage="You do not have permission to connect to a Relativity account"
      >
        <Button variant="primary" onClick={connectRelativity} disabled={isConnecting}>
          {isConnecting ? (
            <>
              <Icon className="lucide--loader animate-spin ease-in-out" />
              Connecting...
            </>
          ) : (
            <>
              <Icon className="lucide--external-link" />
              Connect to Relativity
            </>
          )}
        </Button>
      </PermissionGate>
    </div>
  );

  // The metadata-sync section content depends on auth + job state.
  const renderSyncSection = () => {
    if (isLoadingAuth) {
      return (
        <SettingsSection title="Relativity metadata sync" description={syncDescription}>
          <div className="flex items-center justify-center gap-2 px-5 py-8 text-sm text-gray-400">
            <Icon className="lucide--loader animate-spin ease-in-out" />
            Checking Relativity connection...
          </div>
        </SettingsSection>
      );
    }

    if (!isAuthenticated) {
      return (
        <SettingsSection title="Relativity metadata sync" description={syncDescription}>
          {connectPrompt}
        </SettingsSection>
      );
    }

    // Default setup view
    const startSyncFooter =
      selectedWorkspaceId && preview && preview.eligibleForReSync > 0 && preview.fieldMappingConfigured ? (
        <SettingsSectionFooter className="justify-end py-3.5 pr-3">
          <Button onClick={handleStartSync} disabled={isStarting} variant={'primary'}>
            {isStarting ? (
              <>
                <Icon className="lucide--loader animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Icon className="lucide--refresh-cw" />
                Start Sync
              </>
            )}
          </Button>
        </SettingsSectionFooter>
      ) : undefined;

    return (
      <SettingsSection title="Relativity metadata sync" description={syncDescription} footer={startSyncFooter}>
        {isLoadingWorkspaces ? (
          <SettingsRow title="Workspace">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Icon className="lucide--loader-2 h-4 w-4 animate-spin" />
              Loading workspaces...
            </div>
          </SettingsRow>
        ) : linkedWorkspaceId ? (
          <SettingsRow title="Workspace">
            <div className="flex items-center gap-2.5 pr-1 text-sm">
              <span className="size-1.5 rounded-full bg-green-500" />
              <span className="font-medium text-gray-700">
                {linkedWorkspace?.workspaceName ?? `Workspace ${linkedWorkspaceId}`}
              </span>
            </div>
          </SettingsRow>
        ) : workspaces && workspaces.length > 0 ? (
          <SettingsRow title="Workspace">
            <Select
              value={selectedWorkspaceId?.toString()}
              onValueChange={(value) => setSelectedWorkspaceId(parseInt(value, 10))}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Select a workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((workspace) => (
                  <SelectItem key={workspace.workspaceId} value={workspace.workspaceId.toString()}>
                    {workspace.workspaceName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SettingsRow>
        ) : (
          <div className="px-5 py-4">
            <InfoCard>
              No Relativity workspaces with field mappings configured. Please configure field mappings first in the
              Relativity integration settings.
            </InfoCard>
          </div>
        )}

        {selectedWorkspaceId &&
          (isLoadingPreview ? (
            <SettingsRow title="Files ready to sync">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Icon className="lucide--loader animate-spin ease-in-out" />
                Checking files...
              </div>
            </SettingsRow>
          ) : preview ? (
            <>
              <SettingsRow
                title={
                  <span className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-semibold text-gray-900">
                      {preview.eligibleForReSync.toLocaleString()}
                    </span>
                    <span className="text-sm font-medium text-gray-800">files ready to sync</span>
                  </span>
                }
                description={
                  preview.eligibleForReSync === 0 ? (
                    'No files in this matter have Relativity metadata to re-sync. Metadata is added when documents are imported from Relativity.'
                  ) : preview.skippedNoArtifactId > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      {preview.skippedNoArtifactId.toLocaleString()} file(s) will be skipped due to not being linked to
                      a Relativity document{' '}
                      <Tooltip>
                        <TooltipTrigger>
                          <Icon className="lucide--circle-question-mark text-gray-500" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[25rem]" align="center">
                          To sync a document's metadata, it must have an associated <strong>Artifact ID</strong>. This
                          is automatically assigned when importing directly from Relativity. Contact{' '}
                          <a href="mailto:support@wexler.ai" className="text-blue-500">
                            support@wexler.ai
                          </a>{' '}
                          for more information.
                        </TooltipContent>
                      </Tooltip>
                    </span>
                  ) : undefined
                }
              />

              {!preview.fieldMappingConfigured && (
                <div className="px-5 py-4">
                  <InfoCard>
                    <p>
                      Field mappings aren't configured for this Relativity workspace. Configure field mappings in{' '}
                      <a
                        href="/organisation-settings/integrations/field-mappings"
                        target="_blank"
                        className="inline-flex items-center gap-1 text-blue-500 duration-150 hover:text-blue-600"
                      >
                        organisation settings <Icon className="lucide--arrow-up-right" />
                      </a>{' '}
                      to enable syncing.
                    </p>
                  </InfoCard>
                </div>
              )}
            </>
          ) : null)}
      </SettingsSection>
    );
  };

  return (
    <SettingsContent>
      {renderSyncSection()}

      {/* Parent/child relationship builder — only when authed with a workspace + preview. */}
      {isAuthenticated && selectedWorkspaceId && preview && (
        <SettingsSection title="Build parent/child relationships">
          {relationshipBuildResult && (
            <div className="px-5 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                  <div className="text-lg font-semibold text-green-700">
                    {relationshipBuildResult.newRelationshipsCreated.toLocaleString()}
                  </div>
                  <div className="text-xs text-green-700">New relationships created</div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="text-lg font-semibold text-gray-900">
                    {relationshipBuildResult.existingRelationshipsSkipped.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600">Existing relationships skipped</div>
                </div>
              </div>
            </div>
          )}

          <SettingsRow
            title="Build relationships"
            description={
              <>
                Wexler uses the configured parent identifier field to match child documents to parent Control Numbers.
                Configure this in{' '}
                <Link
                  to="/organisation-settings/integrations/field-mappings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-600"
                >
                  Relativity Field Mappings
                  <Icon className="lucide--arrow-up-right" />
                </Link>
                .
              </>
            }
          >
            <Button
              onClick={handleBuildRelationships}
              disabled={isBuildingRelationships || !preview.fieldMappingConfigured}
            >
              {isBuildingRelationships ? (
                <>
                  <Icon className="lucide--loader animate-spin" />
                  Building...
                </>
              ) : (
                <>
                  <Icon className="lucide--network" />
                  Build Relationships
                </>
              )}
            </Button>
          </SettingsRow>
        </SettingsSection>
      )}

      {/* Load file import — no Relativity login required, gated by org feature flag. */}
      {isLoadFileImportEnabled && (
        <SettingsSection title="Load file import">
          <SettingsRow
            title="Import metadata from a load file"
            description="Upload a Relativity load file (.dat) to map its columns onto this matter's documents. No Relativity login required."
            className="cursor-pointer transition-colors hover:bg-gray-50"
            onClick={() => navigate('load-file-import')}
          >
            <Icon className="lucide--chevron-right mr-2 text-gray-400" />
          </SettingsRow>
        </SettingsSection>
      )}
    </SettingsContent>
  );
}

function RelativitySyncProgress() {
  const { matterId, jobId } = useParams();
  const navigate = useNavigate();

  const { fetchDownload: downloadReport, isLoadingDownload: isDownloadingReport } = useDownloadRelativityReportArtefact(
    matterId ?? '',
  );

  const { progress, complete, error: progressError } = useRelativitySyncProgress(jobId);

  const progressPercentage = useMemo(() => {
    if (complete) return 100;
    if (!progress || progress.totalFiles === 0) return 0;
    return Math.round((progress.processedFiles / progress.totalFiles) * 100);
  }, [progress, complete]);

  const handleDownloadReport = () => {
    if (!complete?.auditTrailId) return;
    downloadReport(complete.auditTrailId);
  };

  return (
    <>
      <SettingsBackLink to=".." label="Back to Relativity" />

      <SettingsContent>
        <div className="max-w-2xl space-y-6">
          <SettingsSectionHeading
            title="Relativity metadata sync"
            description={complete ? 'Sync complete' : 'Syncing metadata from Relativity...'}
            inset={false}
          />

          <div className="space-y-3.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {complete
                  ? 'Completed'
                  : progress?.status === 'preparing'
                    ? progress.preparingMessage || 'Preparing...'
                    : `Processing: ${(progress?.processedFiles ?? 0).toLocaleString()} / ${(progress?.totalFiles ?? 0).toLocaleString()} files`}
              </span>
              <span className="font-medium text-gray-900">{progressPercentage}%</span>
            </div>

            <Progress value={progressPercentage} className="h-2" />
          </div>

          <div className="flex max-w-md gap-12 rounded-lg border bg-gray-50 p-4 px-5">
            <div className="basis-1/3">
              <div className="text-2xl font-semibold text-gray-700">
                {(complete?.matched ?? progress?.matched ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Synced</div>
            </div>
            <div className="basis-1/3">
              <div className="text-2xl font-semibold text-gray-700">
                {(complete?.unmatched ?? progress?.unmatched ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Unmatched</div>
            </div>
            <div className="basis-1/3">
              <div className="text-2xl font-semibold text-gray-700">
                {(complete?.failed ?? progress?.failed ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
          </div>

          {complete && (
            <div className="space-y-4">
              <div className="flex gap-2">
                {complete.auditTrailId && (
                  <Button onClick={handleDownloadReport} disabled={isDownloadingReport}>
                    {isDownloadingReport ? (
                      <>
                        <Icon className="lucide--loader-2 animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Icon className="lucide--download" />
                        Download Report
                      </>
                    )}
                  </Button>
                )}
                <Button onClick={() => navigate('..')}>Done</Button>
              </div>
            </div>
          )}

          {progressError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm text-red-700">{progressError}</p>
              <Button variant="outline" className="mt-2" onClick={() => navigate('..')}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </SettingsContent>
    </>
  );
}
